import ScreenBanner from "../../../../../components/ScreenBanner"
import DisplayNotFindInfo from "../../../../../components/DisplayNotFindInfo";
import {
    Box,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
} from "@mui/material";
import React, { useCallback, useMemo, useRef, useState } from "react";
import PermissionGuard from "../../../../../components/PermissionGuard";
import SearchWithFilters from "../../../../../components/BasicComponents/SearchWithFilters";
import useDebounce from "../../../../../lib/useDebounce";
import { useQuery } from "react-query";
import axiosClient from "../../../../../lib/axios";
import PreFiltros from "../PreFiltrosListagem";
import { getChipFilterItems } from "../../../../../helpers/getChipFilterItems";
import ChipList from "../../../../../components/BasicComponents/ChipList";

export default function ListarTodas() {

    const [palavraChave, setPalavraChave] = useState("");
    const [page, setPage] = useState<number>(0)
    const [rowsPerPage, setRowsPerPage] = useState<number>(10);
    const inputRef = useRef<HTMLInputElement>(null);
    const deboucedSearchTerm = useDebounce(palavraChave, 1000);
    const [filtros, setFiltros] = useState<TPreFilters>({});
    const [openFiltros, setOpenFiltros] = useState(false);

    const filters = useMemo(() => ({
        nome_pesquisar: deboucedSearchTerm,
        ...filtros
    }), [deboucedSearchTerm, filtros])

    const { data } = useQuery(
        ["pesquisar-lojas", page, rowsPerPage, filters],
        async () => {
            const params = {
                page: page + 1,
                limit: rowsPerPage,
                listar_bloqueados: true,
                ...filters
            };

            const cleanParams = Object.fromEntries(
                Object.entries(params).filter(([_, v]) => v !== undefined && v !== '')
            );

            const res = await axiosClient.get("/pesquisar-lojas", { params: cleanParams });
            return res.data;
        },
        {
            keepPreviousData: true,
            refetchOnMount: true
        }
    );

    const handleDeleteFilter = (item: string) => {
        const newFiltros = { ...filtros };

        if (item.startsWith('Departamento:')) {
            delete newFiltros.departamento;
        } else if (item.startsWith('Setor:')) {
            delete newFiltros.setor;
        } else if (item.startsWith('Fabricante:')) {
            delete newFiltros.fabricante;
        } else {
            Object.entries(filtros).forEach(([key, value]) => {
                const label = filtersChipLabels[key as keyof typeof filtersChipLabels] || key;
                const displayValue = Array.isArray(value) ? value.join(', ') : value;
                const chipText = `${label}: ${displayValue}`;

                if (chipText === item) {
                    delete newFiltros[key as keyof TPreFilters];
                }
            });
        }

        setFiltros(newFiltros);
    }

    const handleSearch = useCallback(() => {
        setPage(0);
    }, []);

    const handleChangePage = (_: any, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const lojas = data?.data || [];

    const total = data?.total || 0;

    const chipItems = useMemo(() => {
        return getChipFilterItems(
            filtros,
            filtersChipLabels,
        );
    }, [filtros]);


    return (
        <>
            <ScreenBanner title="Cadastros" />

            <PermissionGuard permissionId="cadastros.loja.listar-todas">
                <Container sx={{ mt: 3 }}>
                    <Typography textAlign="center" variant="h5">Lojas</Typography>
                    <Grid spacing={2} mt={1}>
                        <Grid>
                            <Box component={Paper}>
                                <Paper elevation={2} sx={{ p: 2 }}>
                                    <SearchWithFilters
                                        palavraChave={palavraChave}
                                        textFieldProps={{
                                            inputRef: inputRef,
                                            label: "Pesquisar",
                                            placeholder: "Pesquise por nome",
                                            size: "medium",
                                        }}
                                        pesquisar={handleSearch}
                                        setTermoPesquisa={(value) => {
                                            setPalavraChave(value);
                                            setPage(0);
                                        }}
                                        handleOpenFilters={() => setOpenFiltros(true)}
                                    />
                                    <ChipList
                                        items={chipItems}
                                        onDelete={handleDeleteFilter}
                                    />

                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><b>Código</b></TableCell>
                                                <TableCell><b>Nome</b></TableCell>
                                                <TableCell><b>Razão social</b></TableCell>
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {lojas.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6}>
                                                        <DisplayNotFindInfo label="Nenhum publico encontrado" />
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                lojas.map((publico: any) => (
                                                    <TableRow
                                                        key={publico.id}
                                                        hover
                                                    >
                                                        <TableCell>{publico.id}</TableCell>
                                                        <TableCell>{publico.nome || "-"}</TableCell>
                                                        <TableCell>{publico.razao_social}</TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>

                                    <Stack direction="row" justifyContent="flex-end">
                                        <TablePagination
                                            component="div"
                                            count={total}
                                            page={page}
                                            onPageChange={handleChangePage}
                                            rowsPerPage={rowsPerPage}
                                            onRowsPerPageChange={handleChangeRowsPerPage}
                                            labelRowsPerPage="Linhas por página"
                                        />
                                    </Stack>
                                </Paper>
                            </Box>
                        </Grid>
                        <PreFiltros
                            open={openFiltros}
                            onClose={() => setOpenFiltros(false)}
                            filtros={filtros}
                            onConfirm={(filtros) => {
                                setOpenFiltros(false);
                                setPage(0);
                                setFiltros(filtros);
                            }}
                        />
                    </Grid>
                </Container>
            </PermissionGuard>
        </>
    )
}

export type TPreFilters = {
    [key: string]: any
}
const filtersChipLabels: { [key in keyof TPreFilters]: string } = {
    nome_pesquisar: 'Nome',
    razao_social_pesquisar: 'Razão Social',
    id_pesquisar: 'Código',
};